import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MemeCard from "@/components/feature/MemeCard";
import { SITE_URL, useSeo } from "@/hooks/useSeo";
import { memeCategories, memeItems } from "@/mocks/memes";

export default function Category() {
  const { slug = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [activeTag, setActiveTag] = useState("all");

  const category = memeCategories.find((item) => item.slug === slug);
  const isAll = !category;

  const baseList = useMemo(() => {
    let list = isAll ? memeItems : memeItems.filter((item) => item.category === category?.slug);
    if (keyword) {
      const key = keyword.toLowerCase();
      list = list.filter(
        (item) =>
          item.title.toLowerCase().includes(key) ||
          item.tags.some((tag) => tag.toLowerCase().includes(key)) ||
          item.summary.toLowerCase().includes(key),
      );
    }
    return list;
  }, [isAll, category, keyword]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return Array.from(set).slice(0, 10);
  }, [baseList]);

  const list = useMemo(
    () => (activeTag === "all" ? baseList : baseList.filter((item) => item.tags.includes(activeTag))),
    [baseList, activeTag],
  );

  const pageTitle = keyword
    ? `搜索「${keyword}」的结果 - 131吃瓜`
    : `${isAll ? "全部内容" : category?.name} - 131吃瓜梗图与表情包`;
  const pageDesc = isAll
    ? "131吃瓜全部内容合集，涵盖梗图、表情包、神评论、网友二创与梗合集，支持按标签筛选。"
    : `${category?.desc} 在131吃瓜${category?.name}分类里按标签快速筛选，找到适合当下场景的那一张。`;

  useSeo({
    title: pageTitle,
    description: pageDesc,
    keywords: `${isAll ? "梗合集" : category?.name},131吃瓜,梗图,表情包`,
    path: `/category/${slug}${keyword ? `?q=${encodeURIComponent(keyword)}` : ""}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/category/${slug}#collection`,
          url: `${SITE_URL}/category/${slug}`,
          name: pageTitle,
          description: pageDesc,
          inLanguage: "zh-CN",
          isPartOf: { "@id": `${SITE_URL}/#website` },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
            {
              "@type": "ListItem",
              position: 2,
              name: isAll ? "全部内容" : category?.name,
              item: `${SITE_URL}/category/${slug}`,
            },
          ],
        },
        {
          "@type": "ItemList",
          name: pageTitle,
          itemListElement: list.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `${SITE_URL}/meme/${item.id}`,
            name: item.title,
          })),
        },
      ],
    },
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: isAll ? "全部内容" : category?.name ?? "全部内容" }]} />
          <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">
                {isAll ? "全部内容" : category?.name}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {keyword ? `关于「${keyword}」的搜索结果，共 ${list.length} 条。你可以切换到其它标签继续筛选。` : isAll ? "站内全部内容都在这里，用标签快速缩小范围。" : category?.desc}
              </p>
            </div>
            <Link
              to="/category/all"
              className="inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-layout-grid-line text-sm"></i>
              </span>
              查看全部内容
            </Link>
          </div>

          {allTags.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveTag("all")}
                className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                  activeTag === "all"
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:text-primary-700"
                }`}
              >
                全部标签
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(tag)}
                  className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                    activeTag === tag
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:text-primary-700"
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto w-full px-4 py-12 md:px-8 md:py-16">
          {list.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((item) => (
                <MemeCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 p-12 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-xl text-foreground-950">没找到对应的内容</h2>
              <p className="mt-2 text-sm text-foreground-600">换个关键词，或者去其它分类看看。</p>
              <Link
                to="/"
                className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                返回首页逛逛
              </Link>
            </div>
          )}

          {!isAll && (
            <div className="mt-14 border-t border-background-200 pt-10">
              <h2 className="font-heading text-2xl text-foreground-950">换一类看看</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {memeCategories
                  .filter((item) => item.slug !== slug)
                  .map((item) => (
                    <Link
                      key={item.slug}
                      to={`/category/${item.slug}`}
                      className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-400"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                        <i className={`${item.icon} text-xl`}></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block font-heading text-base text-foreground-950 group-hover:text-accent-700">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-foreground-500">{item.desc}</span>
                      </span>
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}